import React from "react";
import classNames from "classnames";
import {
  Paper,
  FlatButton,
  Popover,
  Checkbox,
  Menu,
  MenuItem
} from "material-ui";
import { PopoverAnimationVertical } from "material-ui/Popover";
import Select from "react-select";
import moment from "moment-timezone";
import ColorHash from "color-hash";
import groupBy from "lodash/groupBy";

import * as util from "../../../util";

import InjectedSelect from "../injected-select.jsx";
import WorkingIndicator from "../working-indicator.jsx";

import connectToStores from "alt-utils/lib/connectToStores";

import EventActions from "../../../actions/EventActions";
import ItemBucketActions from "../../../actions/ItemBucketActions";
import ItemBucketStore from "../../../stores/ItemBucketStore";
import EventStore from "../../../stores/EventStore";
import EventBucketStore from "../../../stores/EventBucketStore";
import UserStore from "../../../stores/UserStore";
import BoxOfficePosFilter from "./box-office-pos-filter.jsx";
import BoxOfficeSundancePosFilter from "./box-office-sundance-pos-filter.jsx";

const colorHasher = new ColorHash({ lightness: 0.6 });

class BoxOfficeSubitemTablePosPicker extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: null, // [null, 'pass_bucket', 'coupon']
      intermediateId: null,

      todayOnly: false,
      eventSort: "upcoming", // upcoming | alphabetical
      lastSelectedSection: null,

      currentEventIds: [],
      refetchIntervalId: null,

      filterText: "",

      activeSalesItemOverlay: null
    };

    this.submit = this.submit.bind(this);
    this.intermediateSubmit = this.intermediateSubmit.bind(this);

    this.selectShowing = this.selectShowing.bind(this);

    this.generateEventOptions = this.generateEventOptions.bind(this);
    this.generateItemBucketOptions = this.generateItemBucketOptions.bind(this);
  }

  static getStores() {
    return [EventStore, EventBucketStore, ItemBucketStore];
  }

  static getPropsFromStores() {
    return {
      getEventsForEventBucket: EventStore.getEventsForEventBucket,
      getItemBucketsForEventBucket:
        ItemBucketStore.getItemBucketsForEventBucket,
      isFetching:
        EventStore.getState().isFetching ||
        ItemBucketStore.getState().isFetching,
      ticketBucketAvailability: EventStore.getState().availability
    };
  }

  _canUseTicket(bucket) {
    if (!bucket.lock_admin_only || UserStore.hasRole("admin")) return true;

    const requiredRoles =
      (bucket.lock_admin_only_role && bucket.lock_admin_only_role.split(",")) ||
      [];

    return UserStore.hasRole(requiredRoles);
  }

  componentDidMount() {
    let eventBucket = EventBucketStore.getEventBucketById(
      this.props.orderData.event_bucket
    );
    let excludeOlderEvents = false;
    if (eventBucket.frontend_config.box_office_performance) {
      excludeOlderEvents = eventBucket.frontend_config.box_office_performance
        .exclude_older_events
        ? true
        : false;
    }
    EventActions.fetchAllForBucket.defer(
      this.props.orderData.event_bucket,
      false,
      excludeOlderEvents
    );
    ItemBucketActions.fetchAllForEventBucket.defer(
      this.props.orderData.event_bucket
    );

    if (!document.getElementById("tailwindloader")) {
      const script = document.createElement("script");
      script.id = "tailwindloader";
      script.src = "https://cdn.tailwindcss.com";
      script.onload = () => {
        tailwind.config = {
          theme: {
            extend: {
              colors: {
                eventiveblue: "#0288d1"
              }
            }
          },
          corePlugins: {
            preflight: false
          }
        };
        this.setState({ hasMounted: true });
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        this.setState({ hasMounted: true });
      }, 100);
    }

    // refetch availability every 5 seconds
    const intervalId = setInterval(() => {
      EventActions.fetchAvailability.defer({
        eventBucketId: this.props.orderData.event_bucket,
        eventIds: this.state.currentEventIds
      });
    }, 5000);

    this.setState(prevState => ({
      ...prevState,
      refetchIntervalId: intervalId
    }));
  }

  componentWillUnmount() {
    if (this.state.refetchIntervalId) {
      clearInterval(this.state.refetchIntervalId);
    }
  }

  componentWillReceiveProps(newProps) {
    const { orderData, updateCurrentOrder } = newProps;

    let subitems = orderData.subitems.slice(0);
    const originalLength = subitems.length;

    // Remove empty quantity subitems
    subitems = subitems.filter(s => {
      return s.quantity > 0 || s.auto;
    });

    if (subitems.length !== originalLength) {
      setTimeout(
        () =>
          updateCurrentOrder({
            subitems
          }),
        0
      );
    }
  }

  submit(id) {
    // id = string => legacy submit path
    // id = object => already a fully formed subitem
    if (this.state.mode === "coupon") {
      this.props.updateCurrentOrder(
        typeof id === "object" && id.code
          ? {
              coupon_code: id.code
            }
          : {
              coupon_id: id
            }
      );
      this.setState({ mode: null });
      return;
    }

    let newSubitem;
    if (typeof id === "string") {
      // For item bucket / ticket bucket with variant
      let variantId;
      if (
        this.state.intermediateId &&
        (this.state.mode === "item_bucket" ||
          this.state.mode === "ticket_bucket")
      ) {
        variantId = id;
        id = this.state.intermediateId;
      }

      newSubitem = {
        [this.state.mode]: id,
        quantity: 1
      };

      if (variantId) {
        newSubitem.variant = variantId;
      }
    } else {
      newSubitem = id;
    }

    let subitems = this.props.orderData.subitems.slice();

    const match = subitems.find(candidate => {
      return Object.keys(newSubitem)
        .concat(Object.keys(candidate))
        .every(k => k === "quantity" || newSubitem[k] === candidate[k]);
    });
    if (match && !match.gift_card) {
      match.quantity += newSubitem.quantity;
    } else {
      subitems.push(newSubitem);
    }

    this.props.updateCurrentOrder({
      subitems: subitems
    });

    // Reset for next item
    this.setState({ mode: null, intermediateId: null });
  }

  intermediateSubmit(id) {
    if (this.state.mode === "item_bucket") {
      this.setState(
        {
          intermediateId: id
        },
        () => {
          let itemBucket = ItemBucketStore.getItemBucketById(id);
          if (itemBucket.variants.length === 1) {
            this.submit(itemBucket.variants[0].id);
          }
        }
      );
    } else if (this.state.mode === "ticket_bucket") {
      let ticketBucket = EventStore.getTicketBucketWithEventById(id);
      if (
        ticketBucket.variants.length === 0 ||
        (ticketBucket.event.venue &&
          ticketBucket.event.venue.use_reserved_seating) // variants will be chosen in seat picker
      ) {
        this.submit(id);
      } else {
        this.setState(
          {
            intermediateId: id
          },
          () => {
            // Note: we don't autosubmit for variants, because there are ALWAYS
            // at least two options, the original bucket + at least one variant.
            // if (ticketBucket.variants.length === 1) {
            //   this.submit(ticketBucket.variants[0].id);
            // }
          }
        );
      }
    }
  }

  _getAvailabilityForTicketBucket(bucket) {
    const availability = this.props.ticketBucketAvailability[bucket.id];

    let quantity_remaining = bucket.quantity_remaining;
    let unlimited = bucket.unlimited;
    let quantity = bucket.quantity;

    if (availability) {
      quantity_remaining = availability.u
        ? Number.MAX_SAFE_INTEGER
        : availability.r;
      unlimited =
        availability.u === undefined ? bucket.unlimited : availability.u;
      quantity =
        availability.a === undefined ? bucket.quantity : availability.a;
    }

    return {
      quantity_remaining,
      unlimited,
      quantity
    };
  }

  _makeQuantityStats(event) {
    return event.ticket_buckets.reduce(
      (data, bucket) => {
        if (bucket.unlimited) {
          data.unlimited = true;
          data.quantity_remaining = Number.MAX_SAFE_INTEGER;
        } else {
          if (!bucket.exclude_capacity) {
            data.quantity_remaining += bucket.quantity_remaining;
            data.quantity += bucket.quantity;
          }
        }

        return data;
      },
      {
        unlimited: false,
        quantity_remaining: 0,
        quantity: 0
      }
    );
  }

  generateEventOptions() {
    let eventBucket = EventBucketStore.getEventBucketById(
      this.props.orderData.event_bucket
    );

    const rawEvents = this.props
      .getEventsForEventBucket(this.props.orderData.event_bucket)
      .filter(e => !e.is_dated || new Date(e.end_time) > new Date())
      .filter(e => {
        if (
          e.name === "Unnamed Virtual Screening" ||
          e.name === "Unnamed Event"
        ) {
          return false;
        }
        return true;
      })
      .filter(e => {
        if (this.props.posConfig.selectedVenueId) {
          if (
            !(e.venue && e.venue.id === this.props.posConfig.selectedVenueId)
          ) {
            return false;
          }
        }
        if (this.props.posConfig.todayOnly) {
          if (
            !(
              e.is_dated &&
              moment()
                .tz(e.timezone)
                .isSame(moment(e.start_time).tz(e.timezone), "day")
            )
          ) {
            return false;
          }
        }

        const isSundance = eventBucket.frontend_config.sundance_login;

        const tagFilterBoolean = isSundance
          ? this.props.posConfig.selectedTagIds &&
            this.props.posConfig.selectedTagIds.length
          : this.props.posConfig.filterByTag &&
            this.props.posConfig.selectedTagIds &&
            this.props.posConfig.selectedTagIds.length;

        if (tagFilterBoolean) {
          if (!e.tags || !e.tags.length) {
            return false;
          }
          if (
            !e.tags.some(t =>
              this.props.posConfig.selectedTagIds.includes(t.id)
            )
          ) {
            return false;
          }
        }

        if (this.state.filterText) {
          if (
            e.name
              .toLowerCase()
              .indexOf(this.state.filterText.toLowerCase()) === -1
          ) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (a.is_virtual && !b.is_virtual) {
          return 1;
        } else if (!a.is_virtual && b.is_virtual) {
          return -1;
        } else if (!a.is_dated && !b.is_dated) {
          return 0;
        } else if (!a.is_dated) {
          return 1;
        } else if (!b.is_dated) {
          return -1;
        }
        return a.start_time.localeCompare(b.start_time);
      });

    this.state.currentEventIds = rawEvents.map(e => e.id);

    if (!rawEvents.length) {
      return [];
    }

    const timezone = rawEvents[0] && rawEvents[0].timezone;
    const today = moment().tz(timezone);

    const groupedEvents = Object.values(
      groupBy(rawEvents, e =>
        e.films.length
          ? e.films
              .map(f => f.id)
              .sort()
              .join("_")
          : e.id
      )
    ).map(events => {
      const baseEvent = events[0];
      const name = baseEvent.name;
      // events.length === 1
      //   ? baseEvent.name
      //   : baseEvent.films.length
      //   ? baseEvent.films.map(f => f.name).join(" & ")
      //   : baseEvent.name;

      const sections = Object.entries(
        groupBy(events, e => {
          if (e.is_virtual) {
            return "virtual";
          } else if (!e.is_dated) {
            return "undated";
          }
          return moment(e.start_time).tz(timezone).format("YYYY-MM-DD");
        })
      ).map(([key, events]) => {
        const dated_events = events.filter(e => e.is_dated);
        const isToday = moment.tz(key, timezone).isSame(today, "day");
        return {
          key,
          ...(dated_events.length
            ? {
                start_time: dated_events[0].start_time,
                end_time: dated_events[dated_events.length - 1].end_time
              }
            : null),
          label:
            key === "undated"
              ? "Undated events"
              : key === "virtual"
                ? "Virtual screenings"
                : `${moment.tz(key, timezone).format("ddd, MMM Do")}${
                    isToday ? " – TODAY" : ""
                  }`,
          showings: events.map(event => {
            const sale_options = [];
            let public_quantity = 0;
            let public_remaining = 0;

            event.ticket_buckets
              .filter(b =>
                this.props.posConfig.publicTicketsOnly ? b.public : true
              )
              .forEach(b => {
                const { quantity_remaining, unlimited, quantity } =
                  this._getAvailabilityForTicketBucket(b);

                b.unlimited = unlimited;
                b.quantity_remaining = quantity_remaining;
                b.quantity = quantity;

                if (this.props.posConfig.publicTicketsOnly) {
                  public_quantity += b.quantity;
                  public_remaining += b.quantity_remaining;
                }

                sale_options.push({
                  key: b.id,
                  subitem: {
                    ticket_bucket: b.id
                  },
                  primary_label: b.name,
                  secondary_label: b.variants.length ? "Default" : undefined,
                  price: b.price,
                  canUse: this._canUseTicket(b),
                  unlimited,
                  quantity,
                  quantity_remaining
                });

                b.variants.forEach(v => {
                  sale_options.push({
                    key: `${b.id}-${v.id}`,
                    subitem: {
                      ticket_bucket: b.id,
                      variant: v.id
                    },
                    primary_label: b.name,
                    secondary_label: v.name,
                    price: v.price,
                    canUse: this._canUseTicket(b),
                    is_variant: true,
                    unlimited,
                    quantity,
                    quantity_remaining
                  });
                });
              });

            const { quantity, quantity_remaining, unlimited } =
              this._makeQuantityStats(event);

            return {
              key: event.id,
              label: event.is_virtual
                ? "Virtual"
                : event.is_dated
                  ? moment(event.start_time).tz(timezone).format("h:mm A")
                  : "Undated",
              quantity_remaining: this.props.posConfig.publicTicketsOnly
                ? public_remaining
                : quantity_remaining,
              quantity: this.props.posConfig.publicTicketsOnly
                ? public_quantity
                : quantity,
              unlimited,
              venue_label:
                (event.venue && event.venue.name) ||
                (event.is_virtual ? "Virtual" : "No venue"),
              sale_options,
              event
            };
          })
        };
      });

      const start = Math.min(...sections.map(s => new Date(s.start_time))),
        end = Math.max(...sections.map(s => new Date(s.end_time))),
        startLabel = start && moment(start).tz(timezone).format("MMM D"),
        endLabel = end && moment(end).tz(timezone).format("MMM D");

      const screening_window_label = [
        startLabel && endLabel
          ? startLabel === endLabel
            ? startLabel
            : `${startLabel} – ${endLabel}`
          : null,
        sections.some(s => s.key === "undated") && "Undated",
        sections.some(s => s.key === "virtual") &&
          (sections.length > 1 ? "Hybrid" : "Virtual")
      ]
        .filter(x => !!x)
        .join(" · ");

      let cover_image = undefined;
      const eventWithCover = events.find(e => e.images.cover);
      if (eventWithCover) {
        cover_image = eventWithCover.images.cover;
      } else {
        const filmWithCover = baseEvent.films.find(
          f => f.still_image || f.cover_image
        );
        if (filmWithCover) {
          cover_image = filmWithCover.still_image || filmWithCover.cover_image;
        }
      }

      return {
        key: baseEvent.id,
        name,
        cover_image,
        screening_window_label,
        sections
      };
    });

    if (this.state.eventSort === "alphabetical") {
      return groupedEvents.sort((a, b) => a.name.localeCompare(b.name));
    }

    return groupedEvents;
  }

  generateItemBucketOptions() {
    let eventBucket = EventBucketStore.getEventBucketById(
      this.props.orderData.event_bucket
    );

    const rawBuckets = this.props
      .getItemBucketsForEventBucket(this.props.orderData.event_bucket)
      .filter(b => {
        if (b.name === "Unnamed Item Bucket") {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    if (
      eventBucket.frontend_config.gift_cards_enabled &&
      this.props.orderData.customerType === "standard"
    ) {
      rawBuckets.unshift({
        id: "gift_card",
        name: "Gift Card",
        code: "gift_card",
        variants: [
          {
            id: "gift_card",
            active: true,
            variable_price: true,
            quantity_remaining: Number.MAX_SAFE_INTEGER
          }
        ]
      });
    }

    const determineDefaultName = code => {
      switch (code) {
        case "gift_card":
          return "Gift Card";
        case "donation":
          return "Donation";
        default:
          return "Default";
      }
    };

    const processed = rawBuckets.map(b => {
      return {
        key: b.id,
        name: b.name,
        sections: [
          {
            key: "default",
            label: "Default",
            showings: b.variants.map(v => {
              return {
                key: v.id,
                label: v.name || determineDefaultName(b.code),
                quantity_remaining: v.quantity_remaining,
                quantity: v.quantity,
                sale_options: [
                  {
                    subitem: {
                      [b.code === "gift_card" ? "gift_card" : "item_bucket"]:
                        b.id,
                      variant: v.id
                    },
                    primary_label: b.name,
                    secondary_label: v.name,
                    price: v.price
                  }
                ]
              };
            })
          }
        ]
      };
    });

    return processed;
  }

  selectShowing(showing) {
    this.setState({ focusedShowing: null });

    const { sale_options } = showing;

    if (sale_options.length === 0) {
      window.alert(`You do not have permission to sell tickets to this event.`);
      return;
    }

    if (sale_options.length === 1) {
      this.submit(
        sale_options[0].subitem.gift_card
          ? {
              ...sale_options[0].subitem,
              quantity: 1,
              name: "Gift Card",
              variable_price: true,
              price: 0
            }
          : { ...sale_options[0].subitem, quantity: 1 }
      );
      return;
    }

    this.setState({ activeSalesItemOverlay: showing });
  }

  getStyles() {
    return {
      container: {
        paddingRight: 20,
        flex: 1,
        display: "flex",
        flexDirection: "column"
      },
      select: {
        display: "none",
        width: "calc(100% - 100px)",
        maxWidth: 300,
        verticalAlign: "middle"
      },
      resetButton: {
        marginLeft: 7,
        verticalAlign: "middle"
      },
      addButton: {
        marginRight: 7,
        border: "1px solid",
        lineHeight: "33px"
      },
      addButtonLast: {
        marginRight: 0
      },

      venueSelectorContainer: {
        display: "flex",
        backgroundColor: "rgba(0,0,0,0.1)",
        padding: "10px 20px",
        borderRadius: 5,
        flexDirection: "column"
      },
      sectionHeader: {
        fontWeight: "bold",
        fontSize: 16,
        marginTop: 20,
        marginBottom: 5
      }
    };
  }

  render() {
    const styles = this.getStyles(),
      now = new Date();

    let eventBucket = EventBucketStore.getEventBucketById(
      this.props.orderData.event_bucket
    );

    // Box office level accounts must enter the exact discount code - no dropdown
    const requireDiscountCodeEntry = UserStore.hasRole("box_office");
    const isSundance = eventBucket.frontend_config.sundance_login;

    const events = this.generateEventOptions();
    const itemBuckets = this.generateItemBucketOptions();

    const { activeSalesItemOverlay } = this.state;

    const FilterComponent = isSundance
      ? BoxOfficeSundancePosFilter
      : BoxOfficePosFilter;

    // only show the loader if the component hasn't finished mounting
    // or if data is being fetched AND there are no events or item buckets
    if (
      !this.state.hasMounted ||
      (this.props.isFetching && events.length === 0 && itemBuckets.length === 0)
    ) {
      return (
        <div style={{ marginTop: 20, width: "100%", textAlign: "center" }}>
          <WorkingIndicator centered />
          <div style={{ marginTop: 10 }}>Preparing the Box Office...</div>
        </div>
      );
    }

    return (
      <div style={styles.container}>
        <FilterComponent
          orderData={this.props.orderData}
          posConfig={this.props.posConfig}
          updatePosConfig={this.props.updatePosConfig}
          filterText={this.state.filterText}
          isSundance={isSundance}
          updateFilterText={text => this.setState({ filterText: text })}
        />
        <div>
          <Popover
            open={!!activeSalesItemOverlay}
            anchorEl={document.getElementById(
              `salesItemOverlayAnchor_${
                activeSalesItemOverlay && activeSalesItemOverlay.key
              }`
            )}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            targetOrigin={{ horizontal: "left", vertical: "top" }}
            onRequestClose={() =>
              this.setState({ activeSalesItemOverlay: null })
            }
            animation={PopoverAnimationVertical}
          >
            <Menu desktop maxHeight={300}>
              {activeSalesItemOverlay &&
                activeSalesItemOverlay.sale_options.map(option => {
                  return (
                    <MenuItem
                      key={option.key}
                      onClick={() => {
                        if (option.quantity_remaining > 0 && option.canUse) {
                          this.submit({ ...option.subitem, quantity: 1 });
                        }
                      }}
                      disabled={
                        option.quantity_remaining <= 0 || !option.canUse
                      }
                      primaryText={
                        <div className="flex">
                          <span className="flex-1">
                            {option.primary_label}
                            {option.secondary_label
                              ? ` –⁠ ${option.secondary_label}`
                              : null}
                            {typeof option.quantity_remaining !== "undefined" &&
                            !option.is_variant ? (
                              <div
                                style={{
                                  marginTop: "-0.35rem",
                                  marginBottom: "0.5rem",
                                  color: "rgba(0,0,0,0.4)",
                                  fontSize: "0.65rem",
                                  lineHeight: "0.65rem"
                                }}
                              >
                                {option.unlimited ? (
                                  "∞ remaining"
                                ) : (
                                  <span>
                                    {option.quantity_remaining} /{" "}
                                    {option.unlimited ? "∞" : option.quantity}{" "}
                                    remaining
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </span>
                          <span className="ml-2">
                            {util.formatPrice(option.price)}
                          </span>
                        </div>
                      }
                    />
                  );
                })}
            </Menu>
          </Popover>
          <div style={styles.sectionHeader} className="flex">
            <span className="flex-1">Events</span>
            <span className="text-sm font-normal rounded border border-gray-200">
              <span
                className={classNames(
                  "cursor-pointer rounded-l px-2 py-0.5",
                  this.state.eventSort === "upcoming"
                    ? "bg-eventiveblue text-white"
                    : "bg-gray-300 text-gray-700"
                )}
                onClick={() => this.setState({ eventSort: "upcoming" })}
              >
                Next Up
              </span>
              <span
                className={classNames(
                  "cursor-pointer rounded-r px-2 py-0.5",
                  this.state.eventSort === "alphabetical"
                    ? "bg-eventiveblue text-white"
                    : "bg-gray-300 text-gray-700"
                )}
                onClick={() => this.setState({ eventSort: "alphabetical" })}
              >
                A-Z
              </span>
            </span>
          </div>
          {events.length ? (
            <div
              style={{
                maxHeight: events.length > 10 ? "50vh" : undefined,
                overflowY: events.length > 10 ? "auto" : undefined,
                marginLeft: -5,
                marginRight: -5
              }}
              onWheel={e => {
                // prevent scrolling the page unless we're at the top or bottom
                if (
                  e.target.scrollTop > 0 &&
                  e.target.scrollTop <
                    e.target.scrollHeight - e.target.offsetHeight
                ) {
                  e.stopPropagation();
                }
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {events.map((e, i) => {
                  return (
                    <EventItem
                      key={e.key}
                      event={e}
                      isLastRow={events.length > 10 && i > events.length - 4}
                      todayOnly={this.props.posConfig.todayOnly}
                      initialSection={this.state.lastSelectedSection}
                      onChangeSection={key => {
                        this.setState({ lastSelectedSection: key });
                      }}
                      onSelectShowing={this.selectShowing}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ fontStyle: "italic" }}>
              {this.state.filterText
                ? "No events matched your search"
                : "No events available for sale"}
            </div>
          )}
        </div>
        <div>
          <div style={styles.sectionHeader}>Items</div>
          {itemBuckets.length ? (
            <div
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
              style={{ marginLeft: -5, marginRight: -5 /*maxHeight: 400*/ }}
            >
              {itemBuckets.map(data => {
                return (
                  <EventItem
                    key={data.key}
                    event={data}
                    todayOnly
                    isItem
                    onSelectShowing={this.selectShowing}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ fontStyle: "italic" }}>
              No items available for sale
            </div>
          )}
        </div>
        <div>
          <div style={styles.sectionHeader}>Other</div>
          <InjectedSelect
            autoFocus={this.state.mode === "pass_bucket"}
            autosize={false}
            style={Object.assign(
              {},
              styles.select,
              this.state.mode === "pass_bucket" && { display: "inline-block" }
            )}
            placeholder="Select pass bucket"
            type="pass_buckets"
            filterBy={p => p.visible_in_box_office}
            labelToFilterString={p => `${p.name}`}
            labelFormatter={p => {
              return (
                <div>
                  <div>
                    <span style={{ fontWeight: "bold" }}>{p.name}</span>
                  </div>
                  <div>
                    {p.unlimited
                      ? "Unlimited passes remaining"
                      : p.quantity - p.quantity_sold !== 1
                        ? `${p.quantity - p.quantity_sold} passes remaining`
                        : "1 pass remaining"}
                  </div>
                </div>
              );
            }}
            isDisabledFunction={p =>
              p.unlimited ? false : p.quantity - p.quantity_sold <= 0
            }
            sortDisabledToEnd={true}
            eventBucketId={this.props.orderData.event_bucket}
            sortFunction={(a, b) =>
              a.rawValue.name.localeCompare(b.rawValue.name)
            }
            onChange={this.submit}
          />
          <InjectedSelect
            autoFocus={this.state.mode === "coupon"}
            autosize={false}
            style={Object.assign(
              {},
              styles.select,
              this.state.mode === "coupon" && { display: "inline-block" }
            )}
            placeholder={
              requireDiscountCodeEntry
                ? "Type discount code (case-sensitive)"
                : "Select discount, or type discount code"
            }
            type={requireDiscountCodeEntry ? null : "coupons"}
            labelFormatter={p => `${p.name}`}
            eventBucketId={this.props.orderData.event_bucket}
            sortFunction={(a, b) => a.label.localeCompare(b.label)}
            onChange={this.submit}
            creatable={true}
            creatablePromptText={s => `Use discount code "${s}"`}
            onCreateSelect={o => this.submit({ code: o.value })}
          />
          {this.state.mode ? (
            <FlatButton
              label="Back"
              onClick={() =>
                this.setState({ mode: null, intermediateId: null })
              }
              style={styles.resetButton}
            />
          ) : (
            <div>
              <FlatButton
                label="+ Pass"
                primary={true}
                onClick={() => this.setState({ mode: "pass_bucket" })}
                style={styles.addButton}
              />
              {!this.props.orderData.coupon ? (
                <FlatButton
                  label={
                    eventBucket.frontend_config.gift_cards_enabled
                      ? "+ Discount / GC"
                      : "+ Discount"
                  }
                  primary={true}
                  onClick={() => this.setState({ mode: "coupon" })}
                  style={Object.assign(
                    {},
                    styles.addButton,
                    styles.addButtonLast
                  )}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }
}

class EventItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSection: null,
      focusedShowing: null
    };
  }

  componentWillReceiveProps(props) {
    if (
      props.initialSection &&
      this.props.event.sections.find(s => s.key === props.initialSection)
    ) {
      this.setState({ selectedSection: props.initialSection });
    }
  }

  _formatQuantity(q) {
    return q >= Number.MAX_SAFE_INTEGER ? "∞" : Math.max(q, 0);
  }

  render() {
    const { event, todayOnly, isItem, isLastRow } = this.props;
    const { name, cover_image, screening_window_label, sections } = event;

    const selectedSection = this.state.selectedSection
      ? sections.find(s => s.key === this.state.selectedSection) || sections[0]
      : sections[0];

    const focusedShowing = selectedSection.showings.find(
      s => s.key === this.state.focusedShowing
    );

    return (
      <Paper
        className="hover:z-10"
        style={{
          padding: 10,
          margin: 5,
          borderRadius: 6,
          position: "relative",
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), ${
            cover_image
              ? `url(${cover_image})`
              : `linear-gradient(${colorHasher.hex(
                  event.key
                )},${colorHasher.hex(event.key)})`
          }`,
          backgroundSize: "cover",
          minHeight: 100
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: "bold",
            color: "#fff"
          }}
          className="max-h-10 overflow-hidden hover:max-h-auto"
        >
          {name}
        </div>
        <div>
          {!todayOnly ? (
            <div style={{ marginBottom: 2, color: "rgba(255,255,255,0.8)" }}>
              {screening_window_label}
            </div>
          ) : null}
          {!todayOnly ? (
            <Select
              style={{
                width: "100%"
              }}
              className="Select--short Select--dark"
              clearable={false}
              searchable={false}
              value={selectedSection.key}
              options={sections.map(s => ({
                value: s.key,
                label: s.label
              }))}
              onChange={data => {
                this.setState({ selectedSection: data.value });
                this.props.onChangeSection(data.value);
              }}
            />
          ) : null}
        </div>
        <div
          style={Object.assign(
            { marginTop: 5, marginLeft: -2, marginRight: -2 }
            //isItem && { maxHeight: 70, overflowY: "auto" }
          )}
        >
          {selectedSection.showings.map(showing => {
            return (
              <div
                key={showing.key}
                id={`salesItemOverlayAnchor_${showing.key}`}
                className={classNames(
                  "rounded",
                  showing.quantity_remaining === 0
                    ? "cursor-not-allowed"
                    : "hover:bg-black hover:bg-opacity-50"
                )}
                style={{ display: "inline-block", margin: 2, maxWidth: "100%" }}
                onMouseEnter={() =>
                  this.setState({ focusedShowing: showing.key })
                }
                onMouseLeave={() => this.setState({ focusedShowing: null })}
              >
                <FlatButton
                  label={showing.label}
                  style={{
                    height: 24,
                    lineHeight: "22px",
                    border: "1px solid #ccc",
                    color: "#fff",
                    backgroundColor: "transparent",
                    pointerEvents:
                      showing.quantity_remaining === 0 ? "none" : undefined,
                    opacity: showing.quantity_remaining === 0 ? 0.5 : undefined,
                    maxWidth: "100%"
                  }}
                  labelStyle={{
                    padding: "0px 5px",
                    fontVariantNumeric: "tabular-nums",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    display: "inline-block",
                    textDecoration:
                      showing.quantity_remaining === 0 ||
                      showing.sale_options.length === 0
                        ? "line-through"
                        : undefined
                  }}
                  disabled={showing.quantity_remaining === 0}
                  onClick={() => this.props.onSelectShowing(showing)}
                />
              </div>
            );
          })}
        </div>
        <div
          id={`eventOverlayAnchor_${event.key}`}
          style={{ position: "relative" }}
        >
          {focusedShowing ? (
            // <Popover
            //   open={true}
            //   anchorEl={document.getElementById(
            //     `eventOverlayAnchor_${event.key}`
            //   )}
            //   // anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            //   // targetOrigin={{ horizontal: "left", vertical: "top" }}
            //   animated={false}
            //   autoCloseWhenOffScreen={false}
            //   canAutoPosition={false}
            //   anchorOrigin={{ horizontal: "left", vertical: "top" }}
            //   style={{ marginTop: 5 }}
            // >
            <div
              style={{
                position: "absolute",
                top: isLastRow ? undefined : 5,
                bottom: isLastRow ? 65 : undefined,
                background: "rgba(255,255,255,0.95)",
                color: "#000",
                borderRadius: 5,
                padding: "5px 8px",
                lineHeight: 1.25,
                zIndex: 999
              }}
              className="shadow"
            >
              {isItem ? (
                <div style={{ fontWeight: "bold" }}>
                  {focusedShowing.label} @{" "}
                  {util.formatPrice(focusedShowing.sale_options[0].price)}
                </div>
              ) : (
                <div style={{ fontWeight: "bold" }}>
                  {(focusedShowing.event && focusedShowing.event.name) || name}{" "}
                  @ {focusedShowing.label}
                </div>
              )}
              <div>
                {this._formatQuantity(focusedShowing.quantity_remaining)}/
                {this._formatQuantity(
                  focusedShowing.quantity || focusedShowing.quantity_remaining
                )}{" "}
                remaining
              </div>
              {focusedShowing.sale_options &&
              focusedShowing.sale_options.length === 1 ? (
                <div>{focusedShowing.sale_options[0].primary_label}</div>
              ) : null}
              <div>{focusedShowing.venue_label}</div>
            </div>
          ) : // </Popover>
          null}
        </div>
      </Paper>
    );
  }
}

export default connectToStores(BoxOfficeSubitemTablePosPicker);



// WEBPACK FOOTER //
// ./src/app/components/partials/box-office/box-office-subitem-table-pos-picker.jsx